/* eslint-disable no-magic-numbers */
import { LINE_COLOR, LINE_WIDTH } from '@/constants/canvas';
import { markersLinesCurrentMapDataSelector } from '@/redux/markers/markers.selectors';
import { allReactionsSelectorOfCurrentMap } from '@/redux/reactions/reactions.selector';
import { MarkerLine, Reaction } from '@/types/models';
import { LinePoint } from '@/types/reactions';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createOverlayLineFeature } from '@/components/Map/MapViewer/utils/config/overlaysLayer/createOverlayLineFeature';
import { getLineFeature } from './getLineFeature';

const getLinePoints = ({ start, end }: Pick<MarkerLine, 'start' | 'end'>): LinePoint => [
  start,
  end,
];

const getReactionsLines = (reactions: Reaction[]): LinePoint[] =>
  reactions.map(({ lines }) => lines.map(getLinePoints)).flat();

export const useOlMapReactionsLayer = (): VectorLayer<VectorSource<Feature<Geometry>>> => {
  const pointToProjection = usePointToProjection();
  const reactions = useSelector(allReactionsSelectorOfCurrentMap);
  const markers = useSelector(markersLinesCurrentMapDataSelector);
  const reactionsLines = getReactionsLines(reactions);

  const reactionsLinesFeatures = useMemo(
    () => reactionsLines.map(linePoint => getLineFeature(linePoint, pointToProjection)),
    [reactionsLines, pointToProjection],
  );

  const markerLinesFeatures = useMemo(
    () =>
      markers.map(marker =>
        createOverlayLineFeature([marker.start, marker.end] as LinePoint, {
          color: marker.color,
          pointToProjection,
        }),
      ),
    [markers, pointToProjection],
  );

  const vectorSource = useMemo(() => {
    return new VectorSource({
      features: [...reactionsLinesFeatures, ...markerLinesFeatures],
    });
  }, [reactionsLinesFeatures, markerLinesFeatures]);

  const reactionsLayer = useMemo(
    () =>
      new VectorLayer({
        source: vectorSource,
        style: new Style({
          fill: new Fill({ color: LINE_COLOR }),
          stroke: new Stroke({ color: LINE_COLOR, width: LINE_WIDTH }),
        }),
      }),
    [vectorSource],
  );

  return reactionsLayer;
};
